/**
 * In-App Purchase Service for FloorPlan Pro
 * Handles pro upgrade purchases for iOS and Android
 */

import { Capacitor } from '@capacitor/core';

// Product ID - must match what you set up in App Store Connect / Google Play Console
export const PRODUCT_ID = 'floorplanpro_unlock';

// Check if we're running on a native device
const isNative = () => Capacitor.isNativePlatform();

// Storage key for pro status
const PRO_STATUS_KEY = 'floorplanpro_pro_status';

/**
 * Initialize the store and register products
 */
export const initializePurchases = async () => {
  if (!isNative()) return false;
  if (!window.CdvPurchase) return false;

  const { store, ProductType, Platform } = window.CdvPurchase;

  try {
    // Register the product
    store.register([{
      id: PRODUCT_ID,
      type: ProductType.NON_CONSUMABLE,
      platform: Platform.GOOGLE_PLAY,
    }, {
      id: PRODUCT_ID,
      type: ProductType.NON_CONSUMABLE,
      platform: Platform.APPLE_APPSTORE,
    }]);

    // Set up event handlers
    store.when()
      .approved(transaction => transaction.verify())
      .verified(receipt => {
        setProStatus(true);
        receipt.finish();
      })
      .finished(() => {});

    // Initialize the store
    await store.initialize([
      Platform.GOOGLE_PLAY,
      Platform.APPLE_APPSTORE,
    ]);

    // Refresh to get product info and restore purchases
    await store.refresh();
    return true;
  } catch {
    return false;
  }
};

/**
 * Get the product info (price, title, etc.)
 */
export const getProductInfo = () => {
  if (!isNative() || !window.CdvPurchase) {
    return {
      id: PRODUCT_ID,
      title: 'FloorPlan Pro',
      description: 'Unlock all pro features',
      price: '$9.99', // Fallback price for display
    };
  }

  const { store } = window.CdvPurchase;
  const product = store.get(PRODUCT_ID);

  if (product) {
    return {
      id: product.id,
      title: product.title || 'FloorPlan Pro',
      description: product.description || 'Unlock all pro features',
      price: product.pricing?.price || '$9.99',
    };
  }

  return {
    id: PRODUCT_ID,
    title: 'FloorPlan Pro',
    description: 'Unlock all pro features',
    price: '$9.99',
  };
};

/**
 * Purchase the pro upgrade
 */
export const purchasePro = async () => {
  if (!isNative()) {
    return { success: false, message: 'Purchases only available in the app' };
  }

  if (!window.CdvPurchase) {
    return { success: false, message: 'Purchase system not available' };
  }

  const { store } = window.CdvPurchase;
  const product = store.get(PRODUCT_ID);

  if (!product) {
    return { success: false, message: 'Product not found' };
  }

  try {
    const offer = product.getOffer();
    if (offer) {
      await offer.order();
      return { success: true, message: 'Purchase initiated' };
    }
    return { success: false, message: 'No offer available' };
  } catch (error) {
    return { success: false, message: error.message || 'Purchase failed' };
  }
};

/**
 * Restore previous purchases
 */
export const restorePurchases = async () => {
  if (!isNative() || !window.CdvPurchase) {
    return { success: false, message: 'Restore only available in the app' };
  }

  const { store } = window.CdvPurchase;

  try {
    await store.refresh();

    // Check if we now have the product
    const product = store.get(PRODUCT_ID);
    if (product?.owned) {
      setProStatus(true);
      return { success: true, message: 'Purchases restored!' };
    }

    return { success: false, message: 'No purchases to restore' };
  } catch (error) {
    return { success: false, message: error.message || 'Restore failed' };
  }
};

/**
 * Check if user has pro status
 */
export const getProStatus = () => {
  // TODO: Remove this bypass before production release
  return true;
};

/**
 * Set pro status (called after successful purchase)
 */
export const setProStatus = (isPro) => {
  try {
    localStorage.setItem(PRO_STATUS_KEY, isPro ? 'true' : 'false');
    window.dispatchEvent(new CustomEvent('proStatusChanged', { detail: { isPro } }));
  } catch {
    // Silently fail if localStorage is unavailable
  }
};

/**
 * Check ownership on app start (for restoring purchases)
 */
export const checkOwnership = async () => {
  if (!isNative() || !window.CdvPurchase) {
    return getProStatus();
  }

  const { store } = window.CdvPurchase;
  const product = store.get(PRODUCT_ID);

  if (product?.owned) {
    setProStatus(true);
    return true;
  }

  return getProStatus();
};
